import { useCallback, useEffect, useRef, useState } from "react";
import { exchangeWhatsAppSignupCode } from "../functions";
const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const FACEBOOK_GRAPH_API_VERSION = "v25.0";
let facebookSdkPromise;
let initializedFacebookAppId;
function isFacebookMessageOrigin(origin) {
    return origin === "https://facebook.com" || origin.endsWith(".facebook.com");
}
function parseMessageData(data) {
    if (typeof data !== "string") {
        return data;
    }
    return JSON.parse(data);
}
function optionalString(value) {
    return typeof value === "string" ? value : undefined;
}
function isBusinessAppOnboardingFinishEvent(event) {
    return event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
}
function initializeFacebookSdk(appId) {
    if (initializedFacebookAppId === appId) {
        return;
    }
    if (!window.FB) {
        throw new Error("Facebook SDK is not available");
    }
    window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: FACEBOOK_GRAPH_API_VERSION,
    });
    initializedFacebookAppId = appId;
}
function loadFacebookSdk(appId) {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return Promise.reject(new Error("Facebook SDK requires a browser"));
    }
    if (!appId) {
        return Promise.reject(new Error("VITE_META_APP_ID is not configured"));
    }
    if (window.FB) {
        try {
            initializeFacebookSdk(appId);
        }
        catch (error) {
            return Promise.reject(error);
        }
        return Promise.resolve();
    }
    if (facebookSdkPromise) {
        return facebookSdkPromise;
    }
    facebookSdkPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${FACEBOOK_SDK_SRC}"]`);
        const script = existingScript ?? document.createElement("script");
        const initializeAndResolve = () => {
            try {
                initializeFacebookSdk(appId);
                resolve();
            }
            catch (error) {
                reject(error);
            }
        };
        window.fbAsyncInit = initializeAndResolve;
        const handleLoad = () => {
            if (window.FB) {
                initializeAndResolve();
                return;
            }
            reject(new Error("Facebook SDK loaded without exposing FB"));
        };
        const handleError = () => reject(new Error("Failed to load Facebook SDK"));
        script.addEventListener("load", handleLoad, { once: true });
        script.addEventListener("error", handleError, { once: true });
        if (!existingScript) {
            script.async = true;
            script.defer = true;
            script.crossOrigin = "anonymous";
            script.src = FACEBOOK_SDK_SRC;
            document.head.appendChild(script);
        }
    }).catch((error) => {
        facebookSdkPromise = undefined;
        throw error;
    });
    return facebookSdkPromise;
}
export function useWhatsAppEmbeddedSignup(_options) {
    const { appId, companyId, configId, debug = false, flow = "new_number", onSessionEvent, } = _options;
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState(null);
    const [loginStatus, setLoginStatus] = useState("idle");
    const [sessionEvent, setSessionEvent] = useState(null);
    const pendingCodeRef = useRef(null);
    const pendingSessionInfoRef = useRef(null);
    const completionStartedRef = useRef(false);
    const completeSignup = useCallback(async () => {
        const authorizationCode = pendingCodeRef.current;
        const sessionInfo = pendingSessionInfoRef.current;
        if (!authorizationCode ||
            !sessionInfo?.wabaId ||
            (!sessionInfo.phoneNumberId &&
                !isBusinessAppOnboardingFinishEvent(sessionInfo.event)) ||
            completionStartedRef.current) {
            return;
        }
        const completeSessionInfo = {
            businessId: sessionInfo.businessId,
            event: sessionInfo.event,
            phoneNumberId: sessionInfo.phoneNumberId,
            wabaId: sessionInfo.wabaId,
        };
        completionStartedRef.current = true;
        setLoginStatus("exchanging_code");
        try {
            await exchangeWhatsAppSignupCode({
                authorizationCode,
                companyId,
                sessionInfo: completeSessionInfo,
            });
            setLoginStatus("code_exchanged");
            setStatus("idle");
        }
        catch {
            setError("Unable to complete WhatsApp onboarding.");
            setLoginStatus("exchange_failed");
            setStatus("error");
        }
        finally {
            pendingCodeRef.current = null;
        }
    }, [companyId]);
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const handleMessage = (event) => {
            if (!isFacebookMessageOrigin(event.origin)) {
                return;
            }
            let data;
            try {
                data = parseMessageData(event.data);
            }
            catch {
                if (debug) {
                    console.debug("[WhatsApp] Ignored non-JSON Facebook message");
                }
                return;
            }
            if (typeof data !== "object" ||
                data === null ||
                data.type !== "WA_EMBEDDED_SIGNUP") {
                return;
            }
            const rawEvent = data;
            const sessionEvent = {
                type: "WA_EMBEDDED_SIGNUP",
                event: optionalString(rawEvent.event),
                version: typeof rawEvent.version === "number" ? rawEvent.version : undefined,
                data: {
                    business_id: optionalString(rawEvent.data?.business_id),
                    event: optionalString(rawEvent.data?.event),
                    phone_number_id: optionalString(rawEvent.data?.phone_number_id),
                    waba_id: optionalString(rawEvent.data?.waba_id),
                },
            };
            if (sessionEvent.data?.waba_id) {
                const eventName = sessionEvent.event ?? sessionEvent.data.event;
                pendingSessionInfoRef.current = {
                    ...pendingSessionInfoRef.current,
                    businessId: sessionEvent.data.business_id ??
                        pendingSessionInfoRef.current?.businessId,
                    event: eventName ?? pendingSessionInfoRef.current?.event,
                    phoneNumberId: sessionEvent.data.phone_number_id ??
                        pendingSessionInfoRef.current?.phoneNumberId,
                    wabaId: sessionEvent.data.waba_id,
                };
                void completeSignup();
            }
            setSessionEvent(sessionEvent);
            onSessionEvent?.(sessionEvent);
            if (debug) {
                console.debug("[WhatsApp] Embedded Signup session event", {
                    type: sessionEvent.type,
                    event: sessionEvent.event,
                    data: sessionEvent.data,
                });
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [completeSignup, debug, onSessionEvent]);
    const fbLoginCallback = useCallback((response) => {
        if (response.authResponse?.code) {
            pendingCodeRef.current = response.authResponse.code;
            void completeSignup();
            return;
        }
        setLoginStatus("cancelled");
        setStatus("idle");
    }, [completeSignup]);
    const launchWhatsAppSignup = useCallback(() => {
        if (!configId) {
            throw new Error("VITE_META_WHATSAPP_CONFIG_ID is not configured");
        }
        if (!window.FB) {
            throw new Error("Facebook SDK is not available");
        }
        const extras = flow === "business_app"
            ? {
                setup: {},
                featureType: "whatsapp_business_app_onboarding",
                sessionInfoVersion: "3",
            }
            : { setup: {} };
        window.FB.login(fbLoginCallback, {
            config_id: configId,
            response_type: "code",
            override_default_response_type: true,
            extras,
        });
    }, [configId, fbLoginCallback, flow]);
    const start = useCallback(async () => {
        setStatus("loading_sdk");
        setError(null);
        setLoginStatus("idle");
        pendingCodeRef.current = null;
        pendingSessionInfoRef.current = null;
        completionStartedRef.current = false;
        try {
            await loadFacebookSdk(appId ?? "");
            launchWhatsAppSignup();
            setStatus("signup_launched");
        }
        catch (loadError) {
            const message = loadError instanceof Error
                ? loadError.message
                : "Unable to load Facebook SDK";
            setError(message);
            setStatus("error");
        }
    }, [appId, launchWhatsAppSignup]);
    const reset = useCallback(() => {
        setStatus("idle");
        setError(null);
        setLoginStatus("idle");
        pendingCodeRef.current = null;
        pendingSessionInfoRef.current = null;
        completionStartedRef.current = false;
    }, []);
    return {
        status,
        error,
        sessionEvent,
        loginStatus,
        fbLoginCallback,
        launchWhatsAppSignup,
        start,
        reset,
    };
}
